// File: apps/web/src/components/common/LanguageSelector.tsx
// Language Selector Component

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { supportedLanguages, changeLanguage } from '../../i18n';

interface LanguageSelectorProps {
  variant?: 'dropdown' | 'list' | 'compact';
  showFlag?: boolean;
  showNativeName?: boolean;
}

export function LanguageSelector({
  variant = 'dropdown',
  showFlag = true,
  showNativeName = true,
}: LanguageSelectorProps) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = supportedLanguages.find(
    (lang) => lang.code === i18n.language
  ) ?? supportedLanguages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectLanguage = (code: string) => {
    changeLanguage(code);
    setIsOpen(false);
  };

  if (variant === 'list') {
    return (
      <div className="space-y-1">
        {supportedLanguages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => handleSelectLanguage(lang.code)}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
              i18n.language === lang.code
                ? 'bg-primary-500/20 text-primary-400'
                : 'text-gray-300 hover:bg-dark-700'
            }`}
          >
            <div className="flex items-center gap-3">
              {showFlag && <span className="text-lg">{lang.flag}</span>}
              <span>{showNativeName ? lang.nativeName : lang.name}</span>
            </div>
            {i18n.language === lang.code && <Check className="w-4 h-4" />}
          </button>
        ))}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 px-2 py-1 text-gray-400 hover:text-white transition-colors"
          aria-label="Select language"
        >
          <Globe className="w-4 h-4" />
          <span className="text-sm uppercase">{currentLanguage.code}</span>
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-40 bg-dark-800 border border-dark-700 rounded-lg shadow-xl z-50">
            {supportedLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleSelectLanguage(lang.code)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors first:rounded-t-lg last:rounded-b-lg ${
                  i18n.language === lang.code
                    ? 'bg-primary-500/20 text-primary-400'
                    : 'text-gray-300 hover:bg-dark-700'
                }`}
              >
                {showFlag && <span>{lang.flag}</span>}
                <span>{lang.nativeName}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Default dropdown variant
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white hover:bg-dark-600 transition-colors"
        aria-label="Select language"
        aria-expanded={isOpen}
      >
        {showFlag && <span className="text-lg">{currentLanguage.flag}</span>}
        <span>{showNativeName ? currentLanguage.nativeName : currentLanguage.name}</span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-dark-800 border border-dark-700 rounded-lg shadow-xl z-50">
          {supportedLanguages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelectLanguage(lang.code)}
              className={`w-full flex items-center justify-between px-3 py-2 text-left transition-colors first:rounded-t-lg last:rounded-b-lg ${
                i18n.language === lang.code
                  ? 'bg-primary-500/20 text-primary-400'
                  : 'text-gray-300 hover:bg-dark-700'
              }`}
            >
              <div className="flex items-center gap-2">
                {showFlag && <span className="text-lg">{lang.flag}</span>}
                <span>{showNativeName ? lang.nativeName : lang.name}</span>
              </div>
              {i18n.language === lang.code && <Check className="w-4 h-4" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default LanguageSelector;
